import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import MegaNav from "./components/site/MegaNav";
import Footer from "./components/site/Footer";
import ConsultationDialog from "./components/site/ConsultationDialog";
import AskGenieDock from "./components/site/AskGenieDock";

import HomePage from "./pages/HomePage";
import DynamicSeriesPage from "./pages/DynamicSeriesPage";
import SolutionsPage from "./pages/SolutionsPage";
import IntelligentLivingPage from "./pages/IntelligentLivingPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import AdminDashboard from "./pages/AdminDashboard";
import ExperiencePage from "./pages/ExperiencePage";
import ValuePacksPage from "./pages/ValuePacksPage";
import ShopPage from "./pages/ShopPage";
import ShopProductPage from "./pages/ShopProductPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import MyOrdersPage from "./pages/MyOrdersPage";
import PolicyPage from "./pages/PolicyPage";

import { ProductRoute, SolutionRoute, LocationRoute } from "./components/seo/routes";
import { BlogIndexPage, BlogArticlePage } from "./components/seo/BlogPages";

import { CartProvider } from "./shop/CartContext";
import { CustomerAuthProvider } from "./shop/AuthContext";
import { CmsProvider } from "./cms/CmsContext";

function PublicShell() {
  const [bookOpen, setBookOpen] = useState(false);
  const openBook = () => setBookOpen(true);

  return (
    <CmsProvider>
    <CartProvider>
      <CustomerAuthProvider>
        <div className="min-h-screen bg-[var(--gx-bg)] text-white">
          <MegaNav onBook={openBook} />
          <Routes>
            <Route path="/" element={<HomePage onBook={openBook} />} />

            {/* Flagship — pinned both in nav and in products dropdown */}
            <Route path="/dynamic-series" element={<DynamicSeriesPage onBook={openBook} />} />

            {/* Value Packs — affordable smart home bundles (2BHK / 3BHK) */}
            <Route path="/value-packs" element={<ValuePacksPage />} />

            {/* Shop — direct-to-consumer catalog + multi-step checkout */}
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/:slug" element={<ShopProductPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/checkout/confirmation/:orderId" element={<OrderConfirmationPage />} />
            <Route path="/account/orders" element={<MyOrdersPage />} />

            {/* Legacy pages preserved */}
            <Route path="/solutions" element={<SolutionsPage onBook={openBook} />} />
            <Route path="/intelligent-living" element={<IntelligentLivingPage onBook={openBook} />} />
            <Route path="/about" element={<AboutPage onBook={openBook} />} />
            <Route path="/contact" element={<ContactPage onBook={openBook} />} />

            {/* Legal / policy pages */}
            <Route path="/privacy-policy" element={<PolicyPage slug="privacy-policy" />} />
            <Route path="/terms-of-service" element={<PolicyPage slug="terms-of-service" />} />
            <Route path="/refund-policy" element={<PolicyPage slug="refund-policy" />} />
            <Route path="/shipping-policy" element={<PolicyPage slug="shipping-policy" />} />
            <Route path="/warranty-policy" element={<PolicyPage slug="warranty-policy" />} />
            <Route path="/cookie-policy" element={<PolicyPage slug="cookie-policy" />} />

            {/* Premium SEO landing routes — driven from data/seoContent.js */}
            <Route path="/products/:slug" element={<ProductRoute onBook={openBook} />} />
            <Route path="/solutions/:slug" element={<SolutionRoute onBook={openBook} />} />
            <Route path="/smart-home/:slug" element={<LocationRoute onBook={openBook} />} />

            {/* Footer-only blog */}
            <Route path="/blog" element={<BlogIndexPage onBook={openBook} />} />
            <Route path="/blog/:slug" element={<BlogArticlePage onBook={openBook} />} />

            <Route path="*" element={<HomePage onBook={openBook} />} />
          </Routes>
          <Footer />
          <AskGenieDock />
          <ConsultationDialog open={bookOpen} onClose={() => setBookOpen(false)} />
        </div>
      </CustomerAuthProvider>
    </CartProvider>
    </CmsProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Hidden admin business OS — fully isolated from public Shell */}
        <Route path="/admin-dashboard/*" element={<AdminDashboard />} />
        {/* Full-screen immersive Experience Center — no MegaNav/Footer */}
        <Route path="/experience" element={<ExperiencePage />} />
        {/* Public marketing site */}
        <Route path="/*" element={<PublicShell />} />
      </Routes>
      <Toaster
        theme="dark"
        position="top-center"
        offset="88px"
        toastOptions={{
          style: {
            background: "rgba(12,12,16,0.92)",
            border: "1px solid var(--gx-border)",
            color: "var(--gx-text)",
          },
        }}
      />
    </BrowserRouter>
  );
}

export default App;
